import { computed, Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CelotexService } from './celotex.service';
import { AuthService } from '../../../core/auth/services/auth.service';
import { LoggerService } from '../../../shared/services/logger.service';
import { Aviso } from '../models';

const DISMISSED_STORAGE_KEY = 'nexus.celotex.dismissed';

/** Entrada persistida: id do aviso + data em que foi dispensado (YYYY-MM-DD). */
interface EntradaDispensada {
  id: string;
  data: string;
}

@Injectable({
  providedIn: 'root',
})
export class CelotexStore {
  private readonly service = inject(CelotexService);
  private readonly auth = inject(AuthService);
  private readonly logger = inject(LoggerService);

  private readonly _avisos = signal<Aviso[]>([]);
  private readonly _isLoading = signal(false);
  private readonly _erro = signal<string | null>(null);

  /**
   * Mapa de id → data de dispensa (YYYY-MM-DD).
   * Um aviso é considerado dispensado apenas se a data de dispensa
   * for igual à data local de hoje. No dia seguinte aparece novamente.
   */
  private readonly _dispensados = signal<Map<string, string>>(
    this._carregarDispensados(),
  );

  readonly isLoading = this._isLoading.asReadonly();
  readonly erro = this._erro.asReadonly();

  /**
   * Avisos filtrados e ordenados:
   * - Apenas com validade >= hoje (data local)
   * - Excluindo dispensados no dia de hoje (voltam amanhã se ainda válidos)
   * - Avisos GERAL: visíveis a todos
   * - Avisos UNIDADE: visíveis apenas para o policial da unidade correspondente
   * - Ordenação: UNIDADE primeiro, depois por validade ascendente
   */
  readonly avisosVisiveis = computed<Aviso[]>(() => {
    const avisos = this._avisos();
    const dispensados = this._dispensados();
    const unidadeCod = this.auth.getUnidadeCod();
    const hoje = this._dataHoje();

    return avisos
      .filter(aviso => {
        const dataDispensa = dispensados.get(aviso.id);
        if (dataDispensa === hoje) return false;
        const validade = new Date(`${aviso.validade}T00:00:00`);
        const dataHoje = new Date(`${hoje}T00:00:00`);
        if (validade < dataHoje) return false;
        if (aviso.id_unidade === null) return true;
        return aviso.id_unidade === unidadeCod;
      })
      .sort((a, b) => {
        if (a.tipo === 'UNIDADE' && b.tipo !== 'UNIDADE') return -1;
        if (a.tipo !== 'UNIDADE' && b.tipo === 'UNIDADE') return 1;
        return a.validade.localeCompare(b.validade);
      });
  });

  async carregar(): Promise<void> {
    this._isLoading.set(true);
    this._erro.set(null);
    await this._fetch();
    this._isLoading.set(false);
  }

  async recarregarSilencioso(): Promise<void> {
    if (this._isLoading()) return;
    await this._fetch();
  }

  /**
   * Dispensa um aviso para hoje.
   * O aviso volta a aparecer amanhã caso ainda esteja dentro da validade.
   */
  dispensar(id: string): void {
    const atual = new Map(this._dispensados());
    atual.set(id, this._dataHoje());
    this._dispensados.set(atual);
    this._persistirDispensados(atual);
  }

  private async _fetch(): Promise<void> {
    try {
      const avisos = await firstValueFrom(this.service.listar());
      this._avisos.set(avisos);
      this._erro.set(null);
      this._limparDispensadosOrfaos(avisos);
    } catch (error) {
      this.logger.error('[CelotexStore] Erro ao carregar avisos', error);
      this._erro.set('Não foi possível carregar os avisos.');
    }
  }

  private _dataHoje(): string {
    const d = new Date();
    const ano = d.getFullYear();
    const mes = String(d.getMonth() + 1).padStart(2, '0');
    const dia = String(d.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  }

  private _carregarDispensados(): Map<string, string> {
    try {
      const raw = localStorage.getItem(DISMISSED_STORAGE_KEY);
      if (!raw) return new Map();
      const entradas = JSON.parse(raw) as EntradaDispensada[];
      if (!Array.isArray(entradas)) return new Map();
      return new Map(entradas.map(e => [e.id, e.data]));
    } catch {
      return new Map();
    }
  }

  private _persistirDispensados(mapa: Map<string, string>): void {
    try {
      const entradas: EntradaDispensada[] = [...mapa.entries()].map(
        ([id, data]) => ({ id, data }),
      );
      localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(entradas));
    } catch {
      /* localStorage indisponível: ignora silenciosamente */
    }
  }

  /** Remove do storage ids que não existem mais no backend (limpeza). */
  private _limparDispensadosOrfaos(avisos: Aviso[]): void {
    const idsValidos = new Set(avisos.map(a => a.id));
    const atuais = this._dispensados();
    const filtrados = new Map([...atuais].filter(([id]) => idsValidos.has(id)));
    if (filtrados.size !== atuais.size) {
      this._dispensados.set(filtrados);
      this._persistirDispensados(filtrados);
    }
  }
}
